import { isCompletedProjectStatus } from "./projectViews";

/**
 * NAV per share (current share value after PNL): API ``share_value_bdt`` is initial/share value
 * plus cumulative approved PNL/share, reconciled with fund NAV and history. Prefer API when present.
 */
export function shareValueBuyPlusPnl(project) {
  const apiVal = project?.share_value_bdt;
  if (apiVal != null && apiVal !== "") {
    const n = Number(apiVal);
    if (Number.isFinite(n)) return n;
  }

  const totalShares = Number(project?.total_shares || 0);
  const fundRaw = project?.total_fund_value;
  const fundVal = fundRaw != null && fundRaw !== "" ? Number(fundRaw) : NaN;
  const current = Number(project?.current_share_price ?? 0);
  const initial = Number(project?.initial_share_price ?? 0);

  let fundNav = null;
  if (totalShares > 0 && Number.isFinite(fundVal) && fundVal > 0) {
    fundNav = fundVal / totalShares;
  }

  if (fundNav != null && current > 0) {
    return Math.max(fundNav, current);
  }
  if (fundNav != null) {
    return fundNav;
  }
  return initial + (current - initial);
}

/** Share value per share: listing / issue price (matches API ``share_buy_value_bdt`` when present). */
export function issuePricePerShare(project) {
  const apiVal = project?.share_buy_value_bdt;
  if (apiVal != null && apiVal !== "") {
    const n = Number(apiVal);
    if (Number.isFinite(n)) return n;
  }
  const initial = Number(project?.initial_share_price ?? 0);
  return Number.isFinite(initial) ? initial : 0;
}

/**
 * Holdings rows should show NAV / value-at-NAV only after primary fundraising (asset-managed funded stage).
 * Status FUNDING never shows NAV—even if the round is fully subscribed—until workflow moves past fundraising.
 */
export function showPortfolioNavMetrics(project) {
  if (!project) return false;
  const st = String(project.status || "").toUpperCase();
  const total = Number(project.total_shares || 0);
  const sold = Number(project.shares_sold || 0);
  const fullySold = total > 0 && sold >= total;
  if (st === "FUNDING" || st === "PAUSED") {
    return sold > 0;
  }
  if (st === "LOCK_IN") return true;
  return fullySold && !isCompletedProjectStatus(project.status);
}

/** Holding cost basis: shares held × average purchase price. */
export function holdingBuyValueTotal(holding) {
  const shares = Number(holding?.shares_held) || 0;
  const avg = Number(holding?.avg_purchase_price) || 0;
  if (!Number.isFinite(shares) || !Number.isFinite(avg)) return 0;
  return shares * avg;
}

/** Holding value at NAV (current share value after PNL). */
export function holdingCurrentValueTotal(holding, project = holding?.project) {
  const nav = shareValueBuyPlusPnl(project);
  const shares = Number(holding?.shares_held) || 0;
  if (!Number.isFinite(nav) || !Number.isFinite(shares)) return 0;
  return shares * nav;
}

/** Shares × listing (issue) price — for primary fundraising before NAV metrics apply. */
export function holdingListingValueTotal(holding, project = holding?.project) {
  const listing = issuePricePerShare(project);
  const shares = Number(holding?.shares_held) || 0;
  if (!Number.isFinite(listing) || !Number.isFinite(shares)) return 0;
  return shares * listing;
}

/** Per-share figure for portfolio copy: NAV when funded; listing during primary fundraising. */
export function portfolioHoldingPricePerShare(project) {
  if (!project) return NaN;
  return showPortfolioNavMetrics(project) ? shareValueBuyPlusPnl(project) : issuePricePerShare(project);
}

/**
 * Primary-market buy price for remaining unsold shares.
 * Listing price until the first primary sale; thereafter current NAV (after approved P&L).
 * Matches backend ``nav_per_share`` on ``purchase_shares``.
 */
export function primaryMarketPurchasePricePerShare(project) {
  const px = portfolioHoldingPricePerShare(project);
  return Number.isFinite(px) ? px : issuePricePerShare(project);
}

/** Total position value for portfolio copy: at NAV when funded; at listing during fundraising. */
export function portfolioHoldingValueTotal(holding, project = holding?.project) {
  if (!holding || !project) return 0;
  return showPortfolioNavMetrics(project)
    ? holdingCurrentValueTotal(holding, project)
    : holdingListingValueTotal(holding, project);
}

/**
 * Dynamic target fund while fundraising: current NAV/share × total share cap.
 * Moves with approved P&L (and persisted fund/NAV fields) until the round is fully subscribed.
 */
export function projectBuyValueFullIssue(project) {
  return projectTargetFundAtNav(project);
}

/** Alias — target fund is always NAV × authorized cap, not listing price × cap. */
export function projectTargetFundAtNav(project) {
  const cap = Number(project?.total_shares || 0);
  if (!Number.isFinite(cap) || cap <= 0) return 0;
  return cap * shareValueBuyPlusPnl(project);
}

/** Total NAV-based value if entire issue priced at NAV (after PNL). */
export function projectCurrentValueFullIssue(project) {
  return projectTargetFundAtNav(project);
}

/** Cumulative approved net P&L for the project (BDT). */
export function projectApprovedNetPnlTotal(project) {
  const api = project?.approved_net_pnl_total_bdt ?? project?.approvedNetPnlTotalBdt;
  if (api != null && api !== "") {
    const n = Number(api);
    if (Number.isFinite(n)) return n;
  }
  const profitPs = Number(project?.share_profit_from_pnl_bdt ?? 0);
  const sold = Number(project?.shares_sold || 0);
  const cap = Number(project?.total_shares || 0);
  const st = String(project?.status || "").toUpperCase();
  const denom = (st === "FUNDING" || st === "PAUSED") && sold > 0 ? sold : cap;
  if (!Number.isFinite(profitPs) || !Number.isFinite(denom) || denom <= 0) return 0;
  return profitPs * denom;
}

/** Primary proceeds at listing: shares sold × initial share price. */
export function projectPrimaryProceedsAtIssuePrice(project) {
  const sold = Number(project?.shares_sold || 0);
  const initial = Number(project?.initial_share_price ?? project?.initialSharePrice ?? 0);
  if (!Number.isFinite(sold) || !Number.isFinite(initial) || sold <= 0) return 0;
  return sold * initial;
}

/** Cash from completed primary purchases (actual subscription proceeds). */
export function projectFundsRaisedCash(project) {
  const api = project?.funds_raised_cash_bdt ?? project?.fundsRaisedCashBdt;
  if (api != null && api !== "") {
    const n = Number(api);
    if (Number.isFinite(n)) return n;
  }
  return projectPrimaryProceedsAtIssuePrice(project);
}

/** Net assets on issued shares at current NAV (official post–P&L mark). */
export function projectNetAssetsIssuedAtNav(project) {
  const api = project?.project_current_fund_bdt ?? project?.projectCurrentFundBdt;
  if (api != null && api !== "") {
    const n = Number(api);
    if (Number.isFinite(n)) return n;
  }
  const sold = Number(project?.shares_sold || 0);
  if (!Number.isFinite(sold) || sold <= 0) return 0;
  return sold * shareValueBuyPlusPnl(project);
}

/** @deprecated Use ``projectNetAssetsIssuedAtNav`` */
export function projectCurrentFundAtIssueBasis(project) {
  return projectNetAssetsIssuedAtNav(project);
}

/** Raised fund at NAV: current share price × primary shares issued. */
export function projectBuyValueSoldApprox(project) {
  const sold = Number(project?.shares_sold || 0);
  return sold * shareValueBuyPlusPnl(project);
}

/** NAV × sold shares (current share value after PNL). */
export function projectCurrentValueSoldShares(project) {
  const sold = Number(project?.shares_sold || 0);
  return sold * shareValueBuyPlusPnl(project);
}

/** Fundraising progress %: subscription cash raised vs target fund at NAV × cap. */
export function fundraisingFundProgressPct(project) {
  const target = projectTargetFundAtNav(project);
  const raised = projectFundsRaisedCash(project);
  if (!Number.isFinite(target) || target <= 0) return 0;
  return Math.min(100, Math.round((raised / target) * 100));
}
